import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { connect, useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Formik, Form as FormikForm, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Button, Card } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import Swal from 'sweetalert2';
import { getUserJoiningDate } from '../../../../services/UsersServices';
import { getUsersSearch } from '../../../../services/BoxService';
import { employeeSearchList } from '../../../../store/selectors/BoxSelectors';
import { employeeSearchAction } from '../../../../store/actions/BoxActions';
import { createTuition } from '../../../../services/TuitionService';

const validationSchema = Yup.object().shape({
  email: Yup.string().required('Employee Name is required'),
  year: Yup.number()
    .integer('Year must be a valid number')
    .min(1900, 'Year must be valid')
    .max(moment().year(), 'Year cannot be in the future')
    .required('Year is required'),

  totalTuitionLimit: Yup.number()
    .min(0, 'Total tuition limit must be positive')
    .required('Total tuition limit is required'),

  usedTuitionAmount: Yup.number()
    .min(0, 'Used tuition amount must be positive')
    .max(Yup.ref('totalTuitionLimit'), 'Used tuition amount cannot exceed total tuition limit')
    .required('Used tuition amount is required'),

  tuitionHistory: Yup.array()
    .of(
      Yup.object().shape({
        enrolledCourses: Yup.string().required('Enrolled courses are required'),
        qualifyingBenefit: Yup.number()
          .min(0, 'Qualifying benefit must be positive')
          .max(Yup.ref('totalTuitionAmount'), 'Qualifying benefit exceeds total tuition')
          .required('Qualifying benefit amount is required'),
        totalTuitionAmount: Yup.number()
          .min(0, 'Total tuition amount must be positive')
          .required('Total tuition amount is required'),
        isApproved: Yup.boolean().default(true).required('Approval status is required'),
      })
    )
    .notRequired(), // Now tuitionHistory is optional
});

const AddTuitionForm = ({ employeeList }) => {
  const initialValues = {
    email: '',
    year: moment().year(),
    totalTuitionLimit: '5250',
    usedTuitionAmount: '0',
    tuitionHistory: [],
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(employeeSearchAction(''));
  }, [dispatch]);

  const loadOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    getUsersSearch(inputValue)
      .then((response) => {
        const options = response.data.map((employee) => ({
          value: employee.displayName || `${employee.firstName} ${employee.lastName}`,
          label: employee.displayName || `${employee.firstName} ${employee.lastName}`,
          email: employee.email,
          employeeNumber: employee.employeeNumber,
        }));
        callback(options);
      })
      .catch((error) => {
        console.error('Error fetching employee data', error);
        callback([]);
      });
  };

  const handleSubmit = async (values) => {
    try {
      await createTuition({ ...values });
      toast.success('Tuition added successfully!', {
        position: 'top-right',
        autoClose: 2000,
      });
      setTimeout(() => navigate('/settings/tuition'), 3000);
    } catch (error) {
      toast.error('Failed to add Tuition!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleEmployeeChange = async (selectedOption, setFieldValue) => {
    setFieldValue(`email`, selectedOption.email);
  };

  return (
    <Card>
      <div className="card-body">
        <Row>
          <Col md={12}>
            <h4>Add Tuition</h4>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue }) => (
                <FormikForm>
                  <Row className="mb-3">
                    <Col>
                      <div className="form-group">
                        <label htmlFor="email">Employee Name</label>
                        <AsyncSelect
                          name="email"
                          loadOptions={loadOptions}
                          defaultOptions={employeeList}
                          placeholder="Select Employee Name"
                          isSearchable
                          onChange={(selectedOption) =>
                            handleEmployeeChange(selectedOption, setFieldValue)
                          }
                        />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col>
                      <div className="form-group">
                        <label htmlFor="year">Year</label>
                        <DatePicker
                          selected={values.year ? new Date(values.year, 0, 1) : null}
                          onChange={(date) => setFieldValue('year', moment(date).year())}
                          showYearPicker
                          dateFormat="yyyy"
                          className="form-control"
                          placeholderText="YYYY"
                        />
                        <ErrorMessage name="year" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <div className="form-group">
                        <label>Total Tuition Limit ($)</label>
                        <Field
                          type="number"
                          name="totalTuitionLimit"
                          className="form-control"
                          min="0.00"
                          step="0.01"
                        />
                        <ErrorMessage
                          name="totalTuitionLimit"
                          component="div"
                          className="text-danger"
                        />
                      </div>
                    </Col>
                    <Col>
                      <div className="form-group">
                        <label>Used Tuition Amount ($)</label>
                        <Field
                          type="number"
                          name="usedTuitionAmount"
                          className="form-control"
                          min="0.00"
                          step="0.01"
                        />
                        <ErrorMessage
                          name="usedTuitionAmount"
                          component="div"
                          className="text-danger"
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* Tuition History Section */}
                  <h5 className="mt-3">Tuition History</h5>
                  <FieldArray
                    name="tuitionHistory"
                    render={(arrayHelpers) => (
                      <div>
                        {values.tuitionHistory.map((history, index) => (
                          <Row key={index} className="mb-2">
                            <Col>
                              <div className="form-group">
                                <label>Enrolled Courses</label>
                                <Field
                                  type="text"
                                  name={`tuitionHistory.${index}.enrolledCourses`}
                                  className="form-control"
                                />
                                <ErrorMessage
                                  name={`tuitionHistory.${index}.enrolledCourses`}
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </Col>

                            <Col>
                              <div className="form-group">
                                <label>Total Tuition Amount ($)</label>
                                <Field
                                  type="number"
                                  name={`tuitionHistory.${index}.totalTuitionAmount`}
                                  className="form-control"
                                  min="0.00"
                                />
                                <ErrorMessage
                                  name={`tuitionHistory.${index}.totalTuitionAmount`}
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </Col>

                            <Col>
                              <div className="form-group">
                                <label>Qualifying Benefit ($)</label>
                                <Field
                                  type="number"
                                  name={`tuitionHistory.${index}.qualifyingBenefit`}
                                  className="form-control"
                                  min="0.00"
                                />
                                <ErrorMessage
                                  name={`tuitionHistory.${index}.qualifyingBenefit`}
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </Col>

                            <Col>
                              <div className="form-group">
                                <label>Approved</label>
                                <Field
                                  as="select"
                                  name={`tuitionHistory.${index}.isApproved`}
                                  className="form-control"
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </Field>
                                <ErrorMessage
                                  name={`tuitionHistory.${index}.isApproved`}
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </Col>

                            <Col md="auto">
                              <Button
                                variant="danger"
                                className="mt-4"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                Remove
                              </Button>
                            </Col>
                          </Row>
                        ))}

                        {/* Button to Add New Tuition Entry */}
                        <Button
                          variant="success"
                          className="mt-2"
                          onClick={() =>
                            arrayHelpers.push({
                              enrolledCourses: '',
                              qualifyingBenefit: 0,
                              totalTuitionAmount: 0,
                              isApproved: true,
                            })
                          }
                        >
                          + Add Tuition Record
                        </Button>
                      </div>
                    )}
                  />

                  <div className="d-flex justify-content-end mt-3">
                    <Button variant="primary" type="submit">
                      Submit
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </Col>
        </Row>
      </div>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  employeeList: employeeSearchList(state),
});

export default connect(mapStateToProps)(AddTuitionForm);
